/**
 * DecimalFieldPreview Component
 *
 * Preview component for decimal field type using Design System TextField.
 *
 * Features: Numeric input (decimals allowed), label display,
 * disabled state for preview. Responsive at narrow widths (< 250px).
 */

import React from 'react';
import { DecimalFieldPreviewProps } from './types';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { DecimalIcon } from 'assests/GmsIcons';
import { useElementWidth } from '../../hooks/useElementWidth';

export const DecimalFieldPreview = ({
  config,
  isSelected,
  onClick,
  fieldName,
}: DecimalFieldPreviewProps) => {
  const { label, displayName, showTitle = true, value = 0 } = config;
  const { containerRef, isNarrow } = useElementWidth();

  // Compute display label following existing UI model pattern
  const displayLabel = showTitle
    ? (displayName || label || fieldName || 'Unnamed Field')
    : '';

  return (
    <div role="button"
      ref={containerRef}
      className={`${isNarrow ? 'tw-p-2' : 'tw-p-4'} tw-border tw-rounded-lg tw-bg-surface tw-cursor-pointer
        tw-transition-all tw-duration-200 hover:tw-shadow-sm
        ${isSelected
          ? 'tw-border-accent tw-ring-2 tw-ring-accent-tint'
          : 'tw-border-edge hover:tw-border-edge-strong'
        }`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      data-testid="decimal-field-preview"
    >
      {/* Header with field name, icon, and type badge */}
      <div className={`tw-flex tw-items-center tw-justify-between tw-gap-2 ${isNarrow ? 'tw-mb-1.5' : 'tw-mb-3'} tw-overflow-hidden`}>
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0 tw-flex-1">
          <span className={`tw-flex-shrink-0 ${isNarrow ? 'tw-w-2.5 tw-h-2.5' : 'tw-w-3 tw-h-3'} tw-flex tw-items-center tw-justify-center tw-text-ink-secondary icon-fill-current`}>
            <DecimalIcon />
          </span>
          {showTitle ? (
            <Typography variant="sub-title-label" className={`tw-text-ink tw-truncate ${isNarrow ? 'tw-text-[10px]' : ''}`}>
              {displayLabel}
            </Typography>
          ) : (
            <Typography variant="sub-title-label" className={`tw-text-ink-tertiary tw-italic tw-truncate ${isNarrow ? 'tw-text-[10px]' : ''}`}>
              Hidden
            </Typography>
          )}
        </div>
        {!isNarrow && (
          <span className="tw-flex-shrink-0 tw-text-[8px] tw-px-1.5 tw-py-px tw-bg-surface-sunken tw-text-ink-secondary tw-rounded-full tw-uppercase tw-font-medium">
            Decimal
          </span>
        )}
      </div>

      {/* Design System TextField with number type - decimals allowed (default) */}
      <TextField
        label=""
        type="number"
        placeholder="0.00"
        value={String(value)}
        disabled
      />
    </div>
  );
};

export default DecimalFieldPreview;
